import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useLayoutEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { INFRACTION_TYPES } from "../../utils/constants/constants";

interface StackedBarChartProps {
  data: any[];
  title: string;
  loading: boolean;
  div: string;
  height: number;
}

const StackedBarChart: React.FC<StackedBarChartProps> = ({
  data,
  title,
  loading,
  div,
  height
}) => {
  useLayoutEffect(() => {
    if (!data) return;

    data = data.map((item) => {
      const [year, month, day] = item.date.split("-");
      if(!day) {
        return {
          ...item,
          date: `${month}/${year}`,
        }
      } else {
        return {
          ...item,
          date: `${day}/${month}`,
        }
      }
    });
    
    let root = am5.Root.new(div);
    let myTheme = am5.Theme.new(root);
    myTheme.rule("Grid", ["base"]).setAll({
      strokeOpacity: 0.1,
    });

    root.setThemes([
      am5themes_Animated.new(root),
      myTheme
    ]);

    let chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      paddingLeft: 20,
      paddingRight: 20,
      layout: root.verticalLayout,
    }));

    let yRenderer = am5xy.AxisRendererY.new(root, {});
    let yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "date",
      renderer: yRenderer,
      tooltip: am5.Tooltip.new(root, {}),
      marginRight: 20
    }));
    yRenderer.grid.template.setAll({
      location: 1
    })

    yAxis.data.setAll(data);
    let xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
      min: 0,
      maxPrecision: 0,
      renderer: am5xy.AxisRendererX.new(root, {
        minGridDistance: 40,
        strokeOpacity: 0.1,
      })
    }));

    let legend = chart.children.push(am5.Legend.new(root, {
      centerX: am5.p50,
      x: am5.p50,
      layout: am5.GridLayout.new(root, {
        maxColumns: 3,
        fixedWidthGrid: true
      })
    }));
    legend.markerRectangles.template.setAll({
      cornerRadiusTL: 10,
      cornerRadiusTR: 10,
      cornerRadiusBL: 10,
      cornerRadiusBR: 10
    });

    for (let i = 0; i < INFRACTION_TYPES.length; i++) {
      const { value, label } = INFRACTION_TYPES[i];
      let series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: label,
        stacked: true,
        xAxis: xAxis,
        yAxis: yAxis,
        baseAxis: yAxis,
        valueXField: value,
        categoryYField: "date",
      }));

      let color;

      switch(label) {
        case "Macros Manuais":
          color = "#DA1E27";
          break;

        case "Macros Alteradas":
          color = "#E98D7F";
          break;

        case "Interjornada":
          color = "#C26388";
          break;

        case "Refeição":
          color = "#8B81C3";
          break;

        case "Horas Extras":
          color = "#75A6C2";
          break;

        case "Tempo de Direção":
          color = "#A2A2A2";
          break;

        default:
          color = "#DA1E27";
          break;
      }
    
      series.columns.template.setAll({
        tooltipText: "{name} {categoryY}: {valueX}",
        tooltipY: am5.percent(90),
        fill: am5.color(color),
        stroke: am5.color(color)
      });
      series.data.setAll(data);
      series.appear();
    
      series.bullets.push(function () {
        return am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            text: "{valueX}",
            fill: root.interfaceColors.get("alternativeText"),
            centerY: am5.p50,
            centerX: am5.p50,
            populateText: true,
            fontSize: 12,
            visible: false
          })
        });
      });
      legend.data.push(series);
    }

  chart.appear(1000, 100);
  return () => root.dispose();
}, [data]);

  return (
    <div>
      <div className="text-dark-blue font-bold text-2xl mb-3">{title}</div>
      <div
        id={div}
        style={{ width: "100%", height: `${height}px`,  display: loading || data.length === 0 ? "none" : "block" }}
      >
      </div>
      {!loading && data.length === 0 ? (
          <div style={{ height: "300px" }} className="flex justify-center items-center h-full">
            Nenhuma dado encontrado
          </div>
        ) : null}
      {loading && <Skeleton height={300} />}
    </div>
  );
};

export default StackedBarChart;
